/**
 * Gives PostgREST error responses that arrive WITHOUT a body one that carries
 * the HTTP status, so `handleSupabaseError` can classify them.
 *
 * Every `{ count: 'exact', head: true }` query is an HTTP HEAD request, and a
 * HEAD response never has a body. When one fails, postgrest-js tries to
 * `JSON.parse('')`, falls back to `{ message: '' }`, and drops the status (it is
 * on the response, not the error). The error handler then sees no code and no
 * message, can't recognise a dead session, and files a "Database" error with
 * `{"message": ""}` — HOK-OS-81 was a 401 from a tab whose session had gone,
 * reported as "Error checking member today check-in".
 *
 * Only REST responses are touched (Auth, Storage and Functions parse their own
 * errors), and only when the body is genuinely empty; a real error body is
 * passed through untouched.
 */

/** The synthesized body; `isUnauthenticatedResponse` reads `httpStatus`. */
export interface PostgrestHttpStatusError {
  message: string;
  code: string;
  details: string | null;
  hint: string | null;
  httpStatus: number;
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

function requestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method.toUpperCase();
  if (typeof input === 'object' && 'method' in input) return input.method.toUpperCase();
  return 'GET';
}

export function withPostgrestStatusBodies(baseFetch: typeof fetch): typeof fetch {
  return async (input, init) => {
    const res = await baseFetch(input, init);
    if (res.ok || res.status < 400 || !requestUrl(input).includes('/rest/v1/')) return res;

    // HEAD responses have no body by definition — don't read one. Otherwise
    // peek at a clone so the original stays readable if it is passed through.
    const body = requestMethod(input, init) === 'HEAD' ? '' : await res.clone().text();
    if (body !== '') return res;

    const error: PostgrestHttpStatusError = {
      message: `HTTP ${res.status}${res.statusText ? ` ${res.statusText}` : ''} (empty response body)`,
      code: '',
      details: null,
      hint: null,
      httpStatus: res.status,
    };
    const headers = new Headers(res.headers);
    headers.delete('content-length');
    headers.set('content-type', 'application/json');
    return new Response(JSON.stringify(error), {
      status: res.status,
      statusText: res.statusText,
      headers,
    });
  };
}
